import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { ITEMS_FOR_VIEW_LIMIT_11 } from 'common/config';
import { ERoute } from 'common/const';
import { ArrowLeftLong, ArrowRightLong } from 'common/svg';
import { useAppSelector } from 'app/store/store.hooks';
import { CatalogCategoryCard } from 'entities/Catalog/components';
import { selectCategoryGoodsCountItems, selectCategoryList, selectIsCategoryLoading } from 'entities/Category/Category.slice';

interface IComponentProps {
  catalogId: number;
  collapsible?: boolean;
}

export const CatalogCategoryList: FC<IComponentProps> = ({ catalogId, collapsible = false }) => {
  // category
  const categoryList = useAppSelector(selectCategoryList);
  const isCategoryLoading = useAppSelector(selectIsCategoryLoading);
  const categoryGoodsCountItems = useAppSelector(selectCategoryGoodsCountItems);

  const [listIsCollapsed, setListIsCollapsed] = useState<boolean>(true);
  const location = useLocation();
  const navigate = useNavigate();

  const visibleCategoryList = collapsible && listIsCollapsed ? categoryList.slice(0, ITEMS_FOR_VIEW_LIMIT_11) : categoryList;
  const hiddenCategoriesCount = categoryList.length - visibleCategoryList.length;
  const showToggle = categoryList.length > ITEMS_FOR_VIEW_LIMIT_11 && collapsible;

  const toggleListIsCollapsed = () => {
    setListIsCollapsed((prev) => !prev);
  };

  // O(1) lookup вместо .find() в .map() (O(n²) по категориям страницы).
  const goodsCountByCategory = useMemo(
    () => new Map(categoryGoodsCountItems.map((item) => [item.categoryId, item.count])),
    [categoryGoodsCountItems],
  );

  // Стабильная identity — иначе React.memo на CatalogCategoryCard бесполезен.
  const changeCategory = useCallback(
    (categoryId: number): void => {
      navigate(`${ERoute.Catalog}/${catalogId}/${categoryId}`, { state: location.state });
    },
    [navigate, catalogId, location.state],
  );

  useEffect(() => {
    setListIsCollapsed(true);
  }, [categoryList]);

  if (!categoryList.length) {
    return null;
  }

  return (
    <div className="redesign grid catalog-category-list">
      {visibleCategoryList.map((category) => (
        <CatalogCategoryCard
          key={category.id}
          category={category}
          goodsCount={goodsCountByCategory.get(category.id) ?? 0}
          loading={isCategoryLoading}
          onClick={changeCategory}
        />
      ))}

      {showToggle &&
        (hiddenCategoriesCount > 0 ? (
          <Button
            className="flex align-center justify-between overflow-hidden catalog-category-list-toggle"
            onClick={toggleListIsCollapsed}
          >
            <span
              className="ff-medium fs-m color-bright-green"
              style={{ zIndex: 1 }}
            >{`Еще ${hiddenCategoriesCount} категорий`}</span>

            <div className="catalog-category-list-toggle-arrow">
              <ArrowRightLong className="icon-arrow-right-long-bright-green" />
            </div>
          </Button>
        ) : (
          <Button
            className="flex align-center justify-between overflow-hidden catalog-category-list-toggle"
            onClick={toggleListIsCollapsed}
          >
            <div className="catalog-category-list-toggle-arrow">
              <ArrowLeftLong className="icon-arrow-left-long-bright-green" />
            </div>

            <span className="ff-medium fs-m color-bright-green" style={{ zIndex: 1 }}>
              Показать меньше
            </span>
          </Button>
        ))}
    </div>
  );
};
